import React, { useCallback, useEffect, useRef, useState } from 'react';
import { notesApi } from '../api/clients';
import { toApiError } from '../api/problem';
import { useI18n } from '../i18n/I18nProvider';
import { NoteTabDto, SaveNoteCommand } from '../web-api-client';
import { CheckIcon, XIcon } from './icons';
import { useToast } from './Toasts';

interface Tab {
  title: string;
  content: string;
}

/**
 * Sổ ghi chú mở từ nút trên thanh trên cùng.
 *
 * Đây là sổ chung của người dùng: RefTableName và RefId đều để trống. Cấu trúc dữ liệu đã sẵn sàng
 * cho sổ gắn vào một bản ghi cụ thể (một todo chẳng hạn) — chỉ cần truyền hai tham số đó xuống là
 * dùng lại được nguyên thành phần này.
 *
 * Cả sổ được gửi lên trong một lần lưu, nên hai tab trình duyệt cùng mở một sổ thì bên lưu sau ghi
 * đè bên lưu trước. Với một cuốn sổ cá nhân thì đánh đổi đó chấp nhận được.
 */
export function NotePanel({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const { showToast } = useToast();

  const [tabs, setTabs] = useState<Tab[]>([]);
  const [active, setActive] = useState(0);
  const [renaming, setRenaming] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const renameRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    let cancelled = false;

    notesApi
      .getNote(undefined, undefined)
      .then(note => {
        if (cancelled) return;

        setTabs((note.description ?? []).map(tab => ({ title: tab.title ?? '', content: tab.content ?? '' })));
      })
      .catch(caught => {
        if (!cancelled) showToast(toApiError(caught).message, { tone: 'danger' });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [showToast]);

  useEffect(() => {
    if (renaming !== null) renameRef.current?.select();
  }, [renaming]);

  const save = useCallback(
    async (next: Tab[]) => {
      setSaving(true);

      try {
        await notesApi.saveNote(
          new SaveNoteCommand({
            description: next.map(tab => new NoteTabDto({ title: tab.title, content: tab.content }))
          })
        );

        setDirty(false);
        return true;
      } catch (caught) {
        showToast(toApiError(caught).message, { tone: 'danger' });
        return false;
      } finally {
        setSaving(false);
      }
    },
    [showToast]
  );

  // Đóng mà còn thay đổi chưa lưu thì lưu nốt rồi mới đóng — sổ ghi chú mà mất chữ vì bấm nhầm dấu
  // nhân thì không ai tha thứ được.
  const close = useCallback(async () => {
    if (dirty && !(await save(tabs))) return;

    onClose();
  }, [dirty, save, tabs, onClose]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  const update = (next: Tab[]) => {
    setTabs(next);
    setDirty(true);
  };

  const addTab = () => {
    const next = [...tabs, { title: t('note.newTabName'), content: '' }];

    update(next);
    setActive(next.length - 1);
    setRenaming(next.length - 1);
  };

  const renameTab = (index: number, title: string) => {
    update(tabs.map((tab, position) => (position === index ? { ...tab, title } : tab)));
  };

  const removeTab = (index: number) => {
    if (!window.confirm(t('note.confirmDeleteTab', { title: tabs[index].title }))) return;

    const next = tabs.filter((_, position) => position !== index);

    update(next);
    setActive(current => Math.max(0, Math.min(current, next.length - 1)));
  };

  const setContent = (content: string) => {
    update(tabs.map((tab, position) => (position === active ? { ...tab, content } : tab)));
  };

  const current = tabs[active];

  return (
    <div className="note-overlay" role="presentation" onMouseDown={close}>
      <div
        className="note-dialog panel"
        role="dialog"
        aria-modal="true"
        aria-label={t('note.title')}
        onMouseDown={event => event.stopPropagation()}
        data-testid="note-panel"
      >
        <header className="note-head">
          <h2 className="note-title">{t('note.title')}</h2>

          <span className="note-state">
            {saving ? t('common.saving') : dirty ? t('note.unsaved') : ''}
          </span>

          <button type="button" className="icon-action" onClick={close} aria-label={t('detail.close')}>
            <XIcon size={16} />
          </button>
        </header>

        <div className="note-tabs" role="tablist">
          {tabs.map((tab, index) =>
            renaming === index ? (
              <input
                key={index}
                ref={renameRef}
                className="note-tab-rename"
                value={tab.title}
                maxLength={100}
                aria-label={t('note.tabName')}
                onChange={event => renameTab(index, event.target.value)}
                onBlur={() => {
                  if (!tab.title.trim()) renameTab(index, t('note.newTabName'));
                  setRenaming(null);
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === 'Escape') event.currentTarget.blur();
                }}
              />
            ) : (
              <button
                key={index}
                type="button"
                role="tab"
                aria-selected={index === active}
                className={`note-tab ${index === active ? 'active' : ''}`}
                onClick={() => setActive(index)}
                onDoubleClick={() => setRenaming(index)}
                title={t('note.renameHint')}
              >
                <span className="note-tab-label">{tab.title}</span>
                <span
                  className="note-tab-close"
                  role="button"
                  tabIndex={-1}
                  aria-label={t('note.deleteTab')}
                  onClick={event => {
                    event.stopPropagation();
                    removeTab(index);
                  }}
                >
                  ×
                </span>
              </button>
            )
          )}

          <button
            type="button"
            className="note-tab-add"
            onClick={addTab}
            aria-label={t('note.addTab')}
            title={t('note.addTab')}
            data-testid="note-add-tab"
          >
            +
          </button>
        </div>

        <div className="note-body">
          {loading ? (
            <p className="text-body-secondary">{t('common.loading')}</p>
          ) : tabs.length === 0 ? (
            <p className="text-body-secondary note-empty">{t('note.empty')}</p>
          ) : (
            <textarea
              ref={contentRef}
              className="form-control note-content"
              placeholder={t('note.contentPlaceholder')}
              value={current?.content ?? ''}
              maxLength={20000}
              onChange={event => setContent(event.target.value)}
              data-testid="note-content"
            />
          )}
        </div>

        <footer className="note-foot">
          <span className="text-body-secondary small">{t('note.renameHint')}</span>

          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={saving || !dirty}
            onClick={async () => {
              if (await save(tabs)) showToast(t('note.saved'), { tone: 'success' });
            }}
          >
            <CheckIcon size={14} /> {t('detail.save')}
          </button>
        </footer>
      </div>
    </div>
  );
}
